import config from 'src/shared/config';
import axios from 'src/shared/utils/axios';
import {
  ApprovalDto,
  ApprovalDetailDto,
  DownloadHistoryInfo,
  DetectedFileInfo,
  FileDetailsInfo,
  FileInfoDto,
  ApprovalType,
} from './approvalApi';
import { UseYN } from '../infra';
import { SendMailsEntity } from './encMailApprovalApi';
import { toBoolean, toUseYN } from 'src/shared/utils/infra';
const FXM_API_URL = config.servers.fxm.url;
export interface ExchangeFullApprovalDto extends ApprovalDto {
  approvalNo: string;
  submission: string;
  subject: string;
  content: string;
  contentType: 'text' | 'html';
  sendSeparate: UseYN;
  priority: UseYN;
  isManualSend: UseYN;
  isAutoSend: UseYN;
  to: string[];
  cc: string[];
  rightView: UseYN;
  rightEdit: UseYN;
  rightPrintScreen: UseYN;
  rightPrint: UseYN;
  rightSave: UseYN;
  allowOffline: UseYN; // 오프라인 사용 방법. 오프라인 사용(Y), 오프라인 차단(N, 온라인)
  allowCnt: number; // 열람 설정 > 사용 횟수, -1 무제한. 나머진 1~12
  allowPcCnt: number; // 열람 설정 > 사용 가능 PC 대수, -1 무제한. 나머진 1~12
  viewNotice: number; //  열람 설정 > 문서 열람 시 전자 메일 알림, 0 받지 않음, 1 한번, 2 매번
  viewStartTs: number; // 열람 설정 > 문서 열람 가능 시간
  viewEndTs: number; // 열람 설정 > 문서 열람 가능 시간
}

export type SendMailStatus = 'ERR' | 'OK' | 'PREPARE';
export interface SendMailsDto {
  mailNo: string; //메일 전송 고유 번호 (필수 값), 최대 64자
  sendStatus: SendMailStatus; // 단체 혹은 개인별 메일 전송 상태, ERR(에러) | OK(성공) | PREPARE(대기)
  approvalNo: string; //결재 고유 번호
  updatedTs: number; //전송 시간
  isZip: UseYN; //zip 전달 유무 (Y zip 전달, N 개별 파일 전달)
  to: string; // 메일 받는 사람. (List 가 아니라 string 으로 ; 로 구분됨, 개별 전달이 아닐 시 a;b;c 와 같이 됨)
}

export interface ExchangeFullApprovalDetailDto extends ApprovalDetailDto {
  submission: string;
  fileDetails: FileDetailsInfo[];
  downloadHistory: DownloadHistoryInfo[];
  detectedFiles?: DetectedFileInfo[];
  sendMails: SendMailsDto[];
  sendStatus: SendMailStatus;
  subject: string; // 메일제목
  content: string; // 메일 내용
  contentType: 'text' | 'html'; //
  sendSeparate: UseYN; // 각각 전송
  priority: UseYN; // 중요 메일 여부
  isManualSend: UseYN; // 수동 전송
  isAutoSend: UseYN; // 자동 전송
  to: string[];
  cc: string[];
  startTs: number;
  endTs: number;
  rightView: UseYN;
  rightEdit: UseYN;
  rightPrintScreen: UseYN;
  rightPrint: UseYN;
  rightSave: UseYN;
  allowOffline: UseYN;
  allowCnt: number;
  allowPcCnt: number;
  viewNotice: number;
  viewStartTs: number;
  viewEndTs: number;
}
export type ExchangeFullApprovalReqDto = ExchangeFullApprovalDto;
export interface ApplyGenKeyDto {
  approvalNo: string;
}
export interface ExchangeFullApprovalDetailEntity
  extends Omit<
    ExchangeFullApprovalDetailDto,
    // | 'code'
    | 'sendMails'
    | 'sendSeparate'
    | 'priority'
    | 'isManualSend'
    | 'isAutoSend'
    | 'rightView'
    | 'rightEdit'
    | 'rightPrintScreen'
    | 'rightPrint'
    | 'rightSave'
    | 'allowOffline'
  > {
  sendMails: SendMailsEntity[];
  sendSeparate: boolean;
  priority: boolean;
  isManualSend: boolean;
  isAutoSend: boolean;
  rightView: boolean;
  rightEdit: boolean;
  rightPrintScreen: boolean;
  rightPrint: boolean;
  rightSave: boolean;
  allowOffline: boolean;
}
export const toExchangeFullApprovalDetailDtoToEntity = (
  dto: ExchangeFullApprovalDetailDto
): ExchangeFullApprovalDetailEntity => {
  return {
    ...dto,
    progressApprovers: dto.progressApprovers === null ? [] : dto.progressApprovers,
    completeApprovers: dto.completeApprovers === null ? [] : dto.completeApprovers,
    sendMails: dto.sendMails.map(
      (mail): SendMailsEntity => ({ ...mail, isZip: mail.isZip === UseYN.Y })
    ),
    sendSeparate: toBoolean(dto.sendSeparate),
    priority: toBoolean(dto.priority),
    isManualSend: toBoolean(dto.isManualSend),
    isAutoSend: toBoolean(dto.isAutoSend),
    rightView: toBoolean(dto.rightView),
    rightEdit: toBoolean(dto.rightEdit),
    rightPrintScreen: toBoolean(dto.rightPrintScreen),
    rightPrint: toBoolean(dto.rightPrint),
    rightSave: toBoolean(dto.rightSave),
    allowOffline: toBoolean(dto.allowOffline),
  };
};
export const toExchangeFullApprovalDetailEntityToDto = (
  entity: ExchangeFullApprovalDetailEntity
): ExchangeFullApprovalDetailDto => {
  return {
    ...entity,
    code: entity.code,
    progressApprovers: entity.progressApprovers === null ? [] : entity.progressApprovers,
    completeApprovers: entity.completeApprovers === null ? [] : entity.completeApprovers,
    sendMails: entity.sendMails.map(
      (mail): SendMailsDto => ({ ...mail, isZip: toUseYN(mail.isZip) })
    ),
    sendSeparate: toUseYN(entity.sendSeparate),
    priority: toUseYN(entity.priority),
    isManualSend: toUseYN(entity.isManualSend),
    isAutoSend: toUseYN(entity.isAutoSend),
    rightView: toUseYN(entity.rightView),
    rightEdit: toUseYN(entity.rightEdit),
    rightPrintScreen: toUseYN(entity.rightPrintScreen),
    rightPrint: toUseYN(entity.rightPrint),
    rightSave: toUseYN(entity.rightSave),
    allowOffline: toUseYN(entity.allowOffline),
  };
};
export interface ExchangeFullMailPreviewReqDto {
  userName: string;
  userId: string;
  subject: string;
  content: string;
  detailEntity: ExchangeFullApprovalDetailEntity | null;
  contentType?: 'text' | 'html';
}
interface ExchangeFullApprovalApi {
  find(approvalNo: string): Promise<ExchangeFullApprovalDetailEntity>; // 사용자 > 내 신청함 > 결재 완료 이외 부분에서 사용하는 API
  find2(approvalNo: string): Promise<ExchangeFullApprovalDetailEntity>; // 사용자 > 내 신청함 > 결재완료에서 사용 하는 API
  getGenKey(): Promise<ApplyGenKeyDto>;
  fileUpload(
    approvalNo: string,
    code: string,
    files: File[],
    progressConfig: {
      onUploadProgress: (progressEvent: any) => void;
    }
  ): Promise<FileInfoDto[]>;
  deleteUploadFile(approvalNo: string, fileUuid: string): Promise<FileInfoDto>;
  deleteUploadFiles(approvalNo: string, fileUuids: string[]): Promise<FileInfoDto[]>;
  apply(reqDto: ExchangeFullApprovalReqDto): Promise<ExchangeFullApprovalDetailDto>;
  cancel(approvalNo: string): Promise<boolean>;
  send(approvalNo: string): Promise<boolean>;
  preview(reqDto: ExchangeFullMailPreviewReqDto): Promise<string>;
}
class ExchangeFullApprovalServerApi implements ExchangeFullApprovalApi {
  async find(approvalNo: string): Promise<ExchangeFullApprovalDetailEntity> {
    const resp = await axios.get<ExchangeFullApprovalDetailDto>(
      `${FXM_API_URL}/api/exchangefull/find/${approvalNo}`
    );
    return toExchangeFullApprovalDetailDtoToEntity(resp.data);
  }
  async find2(approvalNo: string): Promise<ExchangeFullApprovalDetailEntity> {
    const resp = await axios.get<ExchangeFullApprovalDetailDto>(
      `${FXM_API_URL}/api/exchangefull/find2/${approvalNo}`
    );
    return toExchangeFullApprovalDetailDtoToEntity(resp.data);
  }
  async getGenKey(): Promise<ApplyGenKeyDto> {
    const resp = await axios.get<ApplyGenKeyDto>(`${FXM_API_URL}/api/genkey`);
    return resp.data;
  }
  async fileUpload(
    approvalNo: string,
    code: string,
    files: File[],
    progressConfig: {
      onUploadProgress: (progressEvent: any) => void;
    }
  ): Promise<FileInfoDto[]> {
    const form = new FormData();
    form.append('approvalType', 'EXCHANGEFULL');
    form.append('approvalNo', approvalNo);
    form.append('code', code);
    files.map((file) => form.append('files', file));

    const resp = await axios.post<FileInfoDto[]>(
      `${FXM_API_URL}/api/exchangefull/upload`,
      form,
      progressConfig
    );

    return resp.data;
  }

  async deleteUploadFile(approvalNo: string, fileUuid: string): Promise<FileInfoDto> {
    const resp = await axios.get<FileInfoDto>(
      `${FXM_API_URL}/api/exchangefull/delete/one/${approvalNo}/${fileUuid}`
    );
    return resp.data;
  }
  async deleteUploadFiles(approvalNo: string, fileUuids: string[]): Promise<FileInfoDto[]> {
    const resp = await axios.post<FileInfoDto[]>(
      `${FXM_API_URL}/api/exchangefull/delete/many/${approvalNo}`,
      fileUuids
    );
    return resp.data;
  }

  async apply(reqDto: ExchangeFullApprovalReqDto): Promise<ExchangeFullApprovalDetailDto> {
    const resp = await axios.post<ExchangeFullApprovalDetailDto>(
      `${FXM_API_URL}/api/exchangefull/apply`,
      reqDto
    );
    return resp.data;
  }

  async cancel(approvalNo: string): Promise<boolean> {
    await axios.get<ExchangeFullApprovalDetailDto>(
      `${FXM_API_URL}/api/exchangefull/cancel/${approvalNo}`
    );
    return true;
  }

  async send(approvalNo: string): Promise<boolean> {
    await axios.get<void>(`${FXM_API_URL}/api/exchangefull/send/${approvalNo}`);
    return true;
  }
  async preview(reqDto: ExchangeFullMailPreviewReqDto): Promise<string> {
    const res = await axios.post<string>(`${FXM_API_URL}/api/mail/preview`, {
      template: 'fxm_encmail_send', //fxm_encmail_send 고정(FXMv2.3)
      userName: reqDto.userName,
      userId: reqDto.userId,
      subject: reqDto.subject,
      contentType: reqDto.contentType,
      content: reqDto.content,
      approval: reqDto.detailEntity
        ? toExchangeFullApprovalDetailEntityToDto(reqDto.detailEntity)
        : '',
    });
    return res.data;
  }
}
const exchangeFullApprovalApi = new ExchangeFullApprovalServerApi();
export default exchangeFullApprovalApi;
